import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import { isMissingCustomsData } from '@/models/article';
import { ShipmentWithRelations, useCompletePreparation } from '@/models/shipment';
import { AddArticlesModal } from '@/routes/Shipments/Shipment/components/AddArticlesModal';
import useViewPort from '@/utils/useViewport';

import { ShipmentValidationDialog } from './ShipmentValidationDialog';

const PreparationActions = ({
  shipment,
  onValidationDone,
}: {
  shipment: ShipmentWithRelations;
  onValidationDone: () => void;
}) => {
  const { isMobile } = useViewPort();
  const { t } = useLingui();
  const [showErrors, setShowErrors] = useState(false);
  const [error, setError] = useState<string>();

  const {
    mutateAsync: completePreparation,
    isPending: isCompletePreparationPending,
    isSuccess: isCompletePreparationSuccess,
  } = useCompletePreparation();
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [isOpenEditShipmentModal, setIsOpenEditShipmentModal] = useState(false);

  const isMissingCustomsInfo =
    shipment.areCustomsExpected &&
    shipment.articles.some(({ article }) =>
      isMissingCustomsData(article, shipment.organization.config.customs?.type ?? 'fields')
    );

  const isMissingCarrierInfo = !(
    shipment.carrier &&
    shipment.carrierService &&
    (shipment.carrier === 'private-carrier' ||
      shipment.handover === 'automatic_pickup' ||
      shipment.handover === 'dropoff' ||
      (shipment.handover === 'pickup' && shipment.pickupDate))
  );

  return (
    <Footer>
      {(showErrors || !!error) && (
        <FooterError>
          <div style={{ whiteSpace: 'pre-wrap', flex: isMobile ? undefined : '1 1 400px' }}>
            {error}
            {isMissingCustomsInfo ? (
              <Trans id="shipment.missing-customs-info">
                One or more items in this shipment is missing customs info
              </Trans>
            ) : isMissingCarrierInfo ? (
              <Trans id="shipment.missing-carrier-info">
                Please select a carrier and handover option
              </Trans>
            ) : null}
          </div>
        </FooterError>
      )}

      <FooterButtons>
        <Button variant="secondary" onPress={() => setIsOpenEditShipmentModal(true)}>
          <span style={{ textWrap: 'nowrap' }}>
            <Trans id="shipment.add-article">Add items</Trans>
          </span>
        </Button>
        <Button
          variant="primary"
          onPress={() => {
            setShowErrors(false);

            if (isMissingCustomsInfo || isMissingCarrierInfo) {
              setShowErrors(true);

              return;
            }

            setShowValidationDialog(true);
          }}
          isLoading={isCompletePreparationPending || isCompletePreparationSuccess}
        >
          <span style={{ textWrap: 'nowrap' }}>
            {shipment.carrier === 'private-carrier' ? (
              <Trans id="shipment.ready-for-pickup">Mark package preparation as done</Trans>
            ) : (
              <Trans id="shipment.generate-label">Validate and generate the shipping label</Trans>
            )}
          </span>
        </Button>

        <ShipmentValidationDialog
          isOpen={showValidationDialog}
          onOpenChange={setShowValidationDialog}
          isPending={isCompletePreparationPending || isCompletePreparationSuccess}
          shipment={shipment}
          onValidate={() => {
            setError(undefined);

            completePreparation(shipment.id)
              .catch((err) => {
                if (err.message?.includes('cannot be picked up on the selected date')) {
                  setError(
                    t({
                      id: 'shipment.pickup-date-error',
                      message:
                        'The shipment cannot be picked up on the selected date. Please select another date.',
                    })
                  );
                } else if (err.message?.includes('Invalid shipping address')) {
                  setError(
                    t({
                      id: 'shipment.address-error',
                      message:
                        'Invalid shipping address, please review the origin and destination.',
                    })
                  );
                } else {
                  console.error(err);

                  let errorMessage: string;

                  if (err.message.includes('Invalid ship_from.postal_code')) {
                    errorMessage = t({
                      id: 'shipment.error.invalid-origin-postal-code',
                      message: 'Invalid origin postal code, please contact the support',
                    });
                  } else if (err.message.includes('Invalid ship_to.postal_code')) {
                    errorMessage = t({
                      id: 'shipment.error.invalid-destination-postal-code',
                      message: 'Invalid destination postal code, please contact the support',
                    });
                  } else if (err.message.includes('The server encountered a temporary error')) {
                    errorMessage = t({
                      id: 'shipment.error.temporary-server-error',
                      message: 'A temporary error occurred, please try again in 30 seconds',
                    });
                  } else {
                    errorMessage =
                      err.message +
                      '\n' +
                      t({
                        id: 'shipment.persist-error',
                        message: 'If this error persists, please contact Support.',
                      });
                  }

                  const genericErrorMessage = t({
                    id: 'shipment.unknown-error',
                    message: 'Unknown error. Please contact Support',
                  });

                  setError(
                    err.status === 502 || err.status === 400 ? errorMessage : genericErrorMessage
                  );
                }

                // Close the dialog to allow the user to see the error
                setShowValidationDialog(false);
              })
              .then(onValidationDone);
          }}
        />

        <AddArticlesModal
          isOpen={isOpenEditShipmentModal}
          setIsOpen={setIsOpenEditShipmentModal}
          shipment={shipment}
          key={shipment.articles.map((article) => article.article.id).join('')}
        />
      </FooterButtons>
    </Footer>
  );
};

export default PreparationActions;
